import * as React from "react";

/**
 * Bildvorschau im Datei-Fenster: das Bild eingepasst, dazu Verkleinern,
 * Vergrößern, „Einpassen“ und „100 %“ (22.10.2026). Strg/⌘ + Mausrad zoomt.
 *
 * Bewusst ohne pdf.js — ein Bild geht nie durch den PDF-Viewer. Lässt sich die
 * Datei nicht als Bild dekodieren (beschädigt, HEIC unter falschem Namen …),
 * sagt die Ansicht das ehrlich und bietet den Download an, statt eine leere
 * Fläche oder eine PDF-Fehlermeldung zu zeigen.
 */

/** `null` = eingepasst (so groß, wie das Fenster erlaubt, höchstens 100 %). */
export type BildZoom = number | null;

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 8;
const runde = (v: number) => Math.round(v * 100) / 100;

/** Nächste Zoomstufe — von „eingepasst“ aus zählt der tatsächlich gezeigte Maßstab. */
export function naechsterZoom(zoom: BildZoom, eingepasst: number, richtung: 1 | -1): number {
    const basis = zoom ?? eingepasst;
    const faktor = richtung > 0 ? 1.25 : 0.8;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, runde(basis * faktor)));
}

export const BildAnsicht: React.FC<{
    url: string;
    fileName: string;
    onDownload?: () => void;
    /** Zusätzlicher Hinweis über dem Bild (z. B. „lässt sich nicht bearbeiten“). */
    hinweis?: React.ReactNode;
}> = ({ url, fileName, onDownload, hinweis }) => {
    const [zoom, setZoom] = React.useState<BildZoom>(null);
    const [natur, setNatur] = React.useState<{ b: number; h: number } | null>(null);
    const [kaputt, setKaputt] = React.useState(false);
    const buehne = React.useRef<HTMLDivElement | null>(null);
    const [platz, setPlatz] = React.useState<{ b: number; h: number } | null>(null);

    React.useEffect(() => {
        setZoom(null);
        setNatur(null);
        setKaputt(false);
    }, [url]);

    React.useEffect(() => {
        const el = buehne.current;
        if (!el) return;
        const messen = () => setPlatz({ b: el.clientWidth, h: el.clientHeight });
        messen();
        if (typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(messen);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const eingepasst =
        natur && platz && platz.b > 0 && platz.h > 0
            ? Math.min(1, (platz.b - 32) / natur.b, (platz.h - 32) / natur.h)
            : 1;
    const masstab = zoom ?? eingepasst;

    const onWheel = (e: React.WheelEvent) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        setZoom(naechsterZoom(zoom, eingepasst, e.deltaY < 0 ? 1 : -1));
    };

    if (kaputt) {
        return (
            <div className="kvp-rd" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
                <div role="alert">
                    „{fileName}“ lässt sich nicht als Bild anzeigen — die Datei ist beschädigt oder in einem Format, das der
                    Browser nicht kennt.
                </div>
                {onDownload && (
                    <button type="button" className="kvp-rd-btn" onClick={onDownload}>
                        Herunterladen
                    </button>
                )}
            </div>
        );
    }

    const knopf: React.CSSProperties = {
        border: "1px solid rgba(255,255,255,.35)",
        background: "rgba(255,255,255,.08)",
        color: "#fff",
        borderRadius: 6,
        padding: "4px 10px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 13,
    };

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#4a444b" }}>
            <div
                role="toolbar"
                aria-label="Bildansicht"
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", color: "#fff", fontSize: 13 }}
            >
                <button type="button" style={knopf} aria-label="Verkleinern" onClick={() => setZoom(naechsterZoom(zoom, eingepasst, -1))}>
                    −
                </button>
                <span aria-label="Zoom" style={{ minWidth: 48, textAlign: "center" }}>
                    {Math.round(masstab * 100)} %
                </span>
                <button type="button" style={knopf} aria-label="Vergrößern" onClick={() => setZoom(naechsterZoom(zoom, eingepasst, 1))}>
                    +
                </button>
                <button type="button" style={knopf} onClick={() => setZoom(null)} aria-pressed={zoom == null}>
                    Einpassen
                </button>
                <button type="button" style={knopf} onClick={() => setZoom(1)} aria-pressed={zoom === 1}>
                    100 %
                </button>
                {onDownload && (
                    <button type="button" style={{ ...knopf, marginLeft: "auto" }} onClick={onDownload}>
                        Herunterladen
                    </button>
                )}
            </div>
            {hinweis && (
                <div role="status" style={{ padding: "0 12px 6px", color: "#f3e7c4", fontSize: 13 }}>
                    {hinweis}
                </div>
            )}
            <div
                ref={buehne}
                onWheel={onWheel}
                style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex" }}
            >
                <img
                    src={url}
                    alt={fileName}
                    draggable={false}
                    onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth > 0) setNatur({ b: img.naturalWidth, h: img.naturalHeight });
                    }}
                    onError={() => setKaputt(true)}
                    style={{
                        margin: "auto",
                        flex: "none",
                        ...(natur
                            ? { width: Math.round(natur.b * masstab), height: Math.round(natur.h * masstab) }
                            : { maxWidth: "100%", maxHeight: "100%" }),
                    }}
                />
            </div>
        </div>
    );
};
